/**
 * R159ee · Feb 2026 — Settings › Brand Kit Presets
 * ------------------------------------------------------------------
 * List + create + edit + delete named brand kits per workspace. Each
 * kit bundles a palette (up to 5 hex colours), a font pair and an
 * optional logo URL, ready to apply to pitch decks, portal pages and
 * exhibition PDFs so on-brand output never needs manual re-entry.
 *
 * API contract (routes_brand_kits.py):
 *   GET    /workspaces/{ws}/brand-kits          → { rows: [...] }
 *   POST   /workspaces/{ws}/brand-kits          → created row
 *   PATCH  /workspaces/{ws}/brand-kits/{id}     → updated row
 *   DELETE /workspaces/{ws}/brand-kits/{id}     → { ok: true }
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Star, Palette, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { apiErrorMessage } from "@/lib/apiError";

const BLANK_KIT = {
  name: "",
  colors: ["", "", "", "", ""],
  heading_font: "",
  body_font: "",
  logo_url: "",
  is_default: false,
};

function KitEditor({ initial, onSave, onCancel, busy }) {
  const [draft, setDraft] = useState(() => ({
    ...BLANK_KIT,
    ...initial,
    colors: [...(initial?.colors || []), "", "", "", "", ""].slice(0, 5),
  }));
  const patch = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const patchColor = (i, v) => setDraft((d) => {
    const next = [...d.colors];
    next[i] = v;
    return { ...d, colors: next };
  });
  const save = () => {
    const payload = {
      name: draft.name.trim(),
      colors: draft.colors.map((c) => c.trim()).filter(Boolean),
      heading_font: draft.heading_font.trim() || null,
      body_font: draft.body_font.trim() || null,
      logo_url: draft.logo_url.trim() || null,
      is_default: !!draft.is_default,
    };
    if (!payload.name) { toast.error("Name is required"); return; }
    onSave(payload);
  };
  return (
    <div className="border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="brand-kit-editor">
      <label className="block">
        <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Name</span>
        <Input
          value={draft.name}
          onChange={(e) => patch("name", e.target.value)}
          placeholder="e.g. Emerald Pitch, Q4 Campaign, Portal Skin"
          className="rounded-none"
          data-testid="brand-kit-name"
        />
      </label>
      <div>
        <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Colours (up to 5 · hex or CSS colour)</span>
        <div className="flex flex-wrap gap-2">
          {draft.colors.map((c, i) => (
            <div key={i} className="inline-flex items-center gap-1 border border-input bg-white px-1 rounded-none">
              <span
                className="w-4 h-4 border border-neutral-300"
                style={{ background: c || "#f4f4f5" }}
                aria-hidden
              />
              <input
                value={c}
                onChange={(e) => patchColor(i, e.target.value)}
                placeholder="#000000"
                className="w-24 h-7 text-[11px] font-mono focus:outline-none"
                data-testid={`brand-kit-color-${i}`}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Heading font</span>
          <Input
            value={draft.heading_font}
            onChange={(e) => patch("heading_font", e.target.value)}
            placeholder="Inter, Playfair Display…"
            className="rounded-none"
            data-testid="brand-kit-heading-font"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Body font</span>
          <Input
            value={draft.body_font}
            onChange={(e) => patch("body_font", e.target.value)}
            placeholder="Roboto, Source Sans…"
            className="rounded-none"
            data-testid="brand-kit-body-font"
          />
        </label>
      </div>
      <label className="block">
        <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Logo URL (optional)</span>
        <Input
          value={draft.logo_url}
          onChange={(e) => patch("logo_url", e.target.value)}
          placeholder="https://…/logo.svg"
          className="rounded-none"
          data-testid="brand-kit-logo-url"
        />
      </label>
      <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest">
        <input
          type="checkbox"
          checked={!!draft.is_default}
          onChange={(e) => patch("is_default", e.target.checked)}
          data-testid="brand-kit-default"
        />
        Mark as workspace default
      </label>
      <div className="flex items-center gap-2 pt-1">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="brand-kit-save">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          Save
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy} className="rounded-none" data-testid="brand-kit-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default function BrandKitsSettings() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${activeWorkspaceId}/brand-kits`);
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load brand kits"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeWorkspaceId]);

  const createKit = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/brand-kits`, payload);
      toast.success(`Brand kit "${payload.name}" saved`);
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't create brand kit"));
    } finally {
      setBusy(false);
    }
  };

  const updateKit = async (id, payload) => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/brand-kits/${id}`, payload);
      toast.success(`Brand kit updated`);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't update brand kit"));
    } finally {
      setBusy(false);
    }
  };

  const deleteKit = async (id, name) => {
    if (!window.confirm(`Delete brand kit "${name}"?`)) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/brand-kits/${id}`);
      toast.success(`Deleted "${name}"`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't delete brand kit"));
    }
  };

  // R159ff · Feb 2026 — Apply this kit as the workspace's active
  // branding. Copies palette / logo / fonts onto workspace_settings +
  // workspace_branding so every downstream deliverable (pitch decks,
  // portal pages, exhibition PDFs) restyles in one click.
  const [applyingId, setApplyingId] = useState(null);
  const applyKit = async (id, name) => {
    setApplyingId(id);
    try {
      const r = await api.post(
        `/workspaces/${activeWorkspaceId}/brand-kits/${id}/apply`);
      const settingsKeys = Object.keys(r.data?.settings_patch || {}).length;
      const brandingKeys = Object.keys(r.data?.branding_patch || {}).length;
      toast.success(
        `Applied "${name}" · ${settingsKeys} workspace field${settingsKeys === 1 ? "" : "s"}` +
        (brandingKeys ? ` · ${brandingKeys} portal field${brandingKeys === 1 ? "" : "s"}` : ""),
      );
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't apply brand kit"));
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Brand Kits"
        subtitle="Named palette · font · logo bundles that pitch decks, portals and PDFs can apply in one click"
        icon={Palette}
      />
      <div className="flex items-center gap-2">
        {!creating && (
          <Button onClick={() => setCreating(true)} className="rounded-none" data-testid="brand-kit-add">
            <Plus className="w-4 h-4 mr-1" /> New brand kit
          </Button>
        )}
      </div>
      {creating && (
        <KitEditor
          initial={BLANK_KIT}
          onSave={createKit}
          onCancel={() => setCreating(false)}
          busy={busy}
        />
      )}
      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Loading…
        </div>
      ) : rows.length === 0 && !creating ? (
        <EmptyState
          title="No brand kits yet"
          description="Save your first palette + font + logo bundle so team members can apply it to any deliverable in one click."
          icon={Palette}
        />
      ) : (
        <div className="space-y-3" data-testid="brand-kits-list">
          {rows.map((k) => (
            <div key={k.id} className="border border-border bg-white" data-testid={`brand-kit-row-${k.id}`}>
              {editingId === k.id ? (
                <div className="p-3">
                  <KitEditor
                    initial={k}
                    onSave={(payload) => updateKit(k.id, payload)}
                    onCancel={() => setEditingId(null)}
                    busy={busy}
                  />
                </div>
              ) : (
                <div className="p-3 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold truncate" data-testid={`brand-kit-row-name-${k.id}`}>{k.name}</span>
                      {k.is_default && (
                        <span className="inline-flex items-center gap-0.5 border border-amber-400 bg-amber-50 text-amber-700 px-1.5 text-[9px] uppercase tracking-widest">
                          <Star className="w-2.5 h-2.5" /> Default
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mb-2">
                      {(k.colors || []).map((c, i) => (
                        <span
                          key={i}
                          className="w-6 h-6 border border-neutral-200"
                          style={{ background: c }}
                          title={c}
                        />
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground space-x-3">
                      {k.heading_font && <span>H: <span className="font-mono">{k.heading_font}</span></span>}
                      {k.body_font && <span>B: <span className="font-mono">{k.body_font}</span></span>}
                      {k.logo_url && (
                        <a href={k.logo_url} target="_blank" rel="noreferrer" className="text-primary underline">Logo</a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      className="rounded-none h-8 bg-primary hover:bg-primary/90"
                      disabled={applyingId === k.id}
                      onClick={() => applyKit(k.id, k.name)}
                      title="Copy this kit's palette, fonts and logo onto workspace branding so every deliverable adopts the look in one click"
                      data-testid={`brand-kit-apply-${k.id}`}
                    >
                      {applyingId === k.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                      )}
                      Apply
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none h-8"
                      onClick={() => setEditingId(k.id)}
                      data-testid={`brand-kit-edit-${k.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-none h-8 text-rose-700 hover:bg-rose-50"
                      onClick={() => deleteKit(k.id, k.name)}
                      data-testid={`brand-kit-delete-${k.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
